import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom'; 
import { courses } from "../data/Courses";
import { FaArrowLeft, FaSearch, FaSpinner, FaDownload } from "react-icons/fa";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { pdfjs } from "react-pdf";
import PdfViewer from "../components/PdfViewer";
import { Helmet } from 'react-helmet';

const VendorCard = ({ vendorName, onClick }) => {
  return (
    <div
      onClick={() => onClick(vendorName)}
      className="cursor-pointer bg-white shadow-lg rounded-lg p-5 hover:scale-105 transition-all duration-300 ease-in-out transform hover:shadow-2xl"
    >
      <img
        src={
          vendorName === "Mict Seta"
            ? "/images/MictSeta.webp"
            : vendorName === "Soft Skills"
              ? "/images/soft skills.webp"
              : vendorName === "QCTO"
                ? "/images/qcto-2-03.webp"
                  : vendorName === "IRBA"
                    ? "/images/IRBA.png"
                : `/images/${vendorName.toLowerCase().replace(/\s+/g, '')}.webp`
        } 
        alt={vendorName}
        className={`${vendorName === "IRBA" ? "h-32" : "h-20"} object-cover mx-auto mb-6`}
      />
      <p className="text-center font-semibold text-lg text-gray-800">
        {vendorName}
      </p>
    </div>
  );
};

const Courses = () => {
  const navigate = useNavigate();  
  const { vendor } = useParams();  
  const [selectedVendor, setSelectedVendor] = useState(vendor || null);
  const [showCourses, setShowCourses] = useState(vendor !== null);
  const [searchVendor, setSearchVendor] = useState("");
  const [searchCourse, setSearchCourse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false); 

  const btnText = isOpen ? "Close" : "View Training Schedule";

  const backButtonRef = useRef(null);

  useEffect(() => {
    if (!selectedVendor) {
      setShowCourses(false);  
    }
  }, [selectedVendor]);

  const vendors = useMemo(() => [
    "Future Engineers",
    "QCTO",
    "Microsoft",
    "CompTIA",
    "Cisco",
    "IBM",
    "ITIL",
    "POPIA",
    "Certnexus",
    "Soft Skills",
    "Mict Seta",
    "IRBA"
  ], []);

  useEffect(() => {
    console.log("Current vendor param:", vendor);
    console.log("Current vendors list:", vendors);
    
    if (vendor) {
      const vendorLowerCase = vendor.toLowerCase();
      console.log("Vendor lowercase:", vendorLowerCase);
      
      const matchedVendor = vendors.find(v => 
        v.toLowerCase() === vendorLowerCase
      );
      
      console.log("Matched vendor:", matchedVendor);
      
      if (matchedVendor) {
        setSelectedVendor(matchedVendor);
        setShowCourses(true);
      } else {
        console.log(`No vendor match found for: ${vendor}`);
        console.log('Available vendors:', vendors);
        
        setShowCourses(false);
        setSelectedVendor(null);
      }
    }
  }, [vendor, vendors]);

  const trendRank = {
    "Future Engineers": 1,
    QCTO: 2,
    Microsoft: 3,
    CISCO: 4,
    CompTIA: 5,
    ITIL: 6,
    IBM: 7,
    POPIA: 8,
    Certnexus: 9,
    "Mict Seta": 10,
    "Soft Skills": 11,
    "IRBA": 12,
  };

  const sortedVendors = vendors.sort((a, b) => trendRank[a] - trendRank[b]);

  const filteredVendors = useMemo(() => {
    setIsLoading(true); 
    const result = vendors.filter((vendor) =>
      vendor.toLowerCase().includes(searchVendor.toLowerCase())
    );
    setIsLoading(false); 
    return result;
  }, [searchVendor]);

  const filteredCourses = useMemo(() => {
    setIsLoading(true); 
    if (!selectedVendor) {
      setIsLoading(false); 
      return [];
    }
    const result = courses.filter(
      (course) =>
        course.categories.includes(selectedVendor) &&
        (course.displayName
          .toLowerCase()
          .includes(searchCourse.toLowerCase()) ||
          course.categories.some((category) =>
            category.toLowerCase().includes(searchCourse.toLowerCase())
          ))
    );
    setIsLoading(false); 
    return result;
  }, [selectedVendor, searchCourse]);

  const handleVendorClick = (vendorName) => {
    if (vendorName === "Future Engineers") {
      window.open("https://fep-future-engineers.netlify.app/", "_blank", "noopener,noreferrer");
      return;
    }
    setSelectedVendor(vendorName);
    setSearchVendor('');
    setSearchCourse('');
    setShowCourses(true);
    navigate(`/courses/${vendorName.toLowerCase()}`);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleBackButtonClick = () => {
    setShowCourses(false); 
    setSelectedVendor(null); 
    setSearchVendor(''); 
    setSearchCourse('');  
    navigate("/courses");  
  };

  const getMetaTags = () => {
    if (selectedVendor) {
      return (
        <Helmet>
          <title>{selectedVendor} Training Courses | Ikusasa Technology Solutions</title>
          <meta name="description" content={`Explore our comprehensive ${selectedVendor} training courses and certification programs. Enhance your skills with professional IT training from Ikusasa Technology Solutions.`} />
          <meta name="keywords" content={`${selectedVendor} training, ${selectedVendor} certification, IT courses, professional training, technology education, ${selectedVendor.toLowerCase()} skills`} />
          <link rel="canonical" href={`https://ikusasatech.com/courses/${selectedVendor.toLowerCase()}`} />
          <meta property="og:title" content={`${selectedVendor} Training Courses | Ikusasa Technology Solutions`} />
          <meta property="og:description" content={`Explore our comprehensive ${selectedVendor} training courses and certification programs. Enhance your skills with professional IT training.`} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://ikusasatech.com/courses/${selectedVendor.toLowerCase()}`} />
          <meta property="og:image" content={`https://ikusasatech.com/images/${selectedVendor.toLowerCase()}.webp`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${selectedVendor} Training Courses | Ikusasa Technology Solutions`} />
          <meta name="twitter:description" content={`Explore our comprehensive ${selectedVendor} training courses and certification programs. Enhance your skills with professional IT training.`} />
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "ItemList",
                "itemListElement": [
                  ${filteredCourses.slice(0, 10).map((course, index) => `
                    {
                      "@type": "Course",
                      "position": ${index + 1},
                      "url": "https://ikusasatech.com/Courses/schedule/${course.originalName}",
                      "name": "${course.displayName}",
                      "description": "${course.displayName} training by Ikusasa Technology Solutions",
                      "provider": {
                        "@type": "Organization",
                        "name": "Ikusasa Technology Solutions",
                        "sameAs": "https://ikusasatech.com"
                      }
                    }
                  `).join(',')}
                ]
              }
            `}
          </script>
        </Helmet>
      );
    } else {
      return (
        <Helmet>
          <title>Professional IT Training Courses | Ikusasa Technology Solutions</title>
          <meta name="description" content="Discover our wide range of professional IT training courses and certification programs from leading vendors including Microsoft, CompTIA, Cisco, IBM, and more. Enhance your career with expert-led training." />
          <meta name="keywords" content="IT training, certification courses, Microsoft training, CompTIA certification, Cisco courses, IBM certification, professional development, technology education, IT skills" />
          <link rel="canonical" href="https://ikusasatech.com/courses" />
          <meta property="og:title" content="Professional IT Training Courses | Ikusasa Technology Solutions" />
          <meta property="og:description" content="Discover our wide range of professional IT training courses and certification programs from leading vendors including Microsoft, CompTIA, Cisco, IBM, and more." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://ikusasatech.com/courses" />
          <meta property="og:image" content="https://ikusasatech.com/android-chrome-192x192.webp" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Professional IT Training Courses | Ikusasa Technology Solutions" />
          <meta name="twitter:description" content="Discover our wide range of professional IT training courses and certification programs from leading vendors including Microsoft, CompTIA, Cisco, IBM, and more." />
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "ItemList",
                "itemListElement": [
                  ${vendors.slice(0, 10).map((vendor, index) => `
                    {
                      "@type": "ListItem",
                      "position": ${index + 1},
                      "url": "https://ikusasatech.com/courses/${vendor.toLowerCase()}",
                      "name": "${vendor} Training"
                    }
                  `).join(',')}
                ]
              }
            `}
          </script>
        </Helmet>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      {getMetaTags()}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div>
          {/* Search bar for vendor cards */}
          {!showCourses && (
            <div className="mb-6 max-w-lg mx-auto relative">
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchVendor}
                onChange={(e) => setSearchVendor(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            </div>
          )}

          {/* If courses are not visible, show vendor cards */}
          {!showCourses ? (
            <div>
              {/* 2027 public training schedule */}
              <div className="mb-8 bg-white shadow-md rounded-lg p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <h2 className="text-lg font-semibold text-gray-900">2027 Public Training Schedule</h2>
                  <p className="text-sm text-gray-600">See upcoming course dates for the year ahead.</p>
                </div>
                <div className="flex gap-3">
                  <a
                    href="/Ikusasa-2027-Public-Schedule.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-300"
                  >
                    View Schedule
                  </a>
                  <a
                    href="/Ikusasa-2027-Public-Schedule.pdf"
                    download="Ikusasa-2027-Public-Schedule.pdf"
                    className="inline-flex items-center gap-2 border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition duration-300"
                  >
                    <FaDownload /> Download
                  </a>
                </div>
              </div>

              {/* Display message when no vendors are found */}
              {filteredVendors.length === 0 ? (
                <p className="text-center text-gray-500 text-xl">
                  No vendors found for this search.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 mb-8">
                  {filteredVendors.map((vendor) => (
                    <VendorCard
                      key={vendor}
                      vendorName={vendor}
                      onClick={handleVendorClick}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <button
                onClick={handleBackButtonClick}
                className="bg-blue-300 text-white px-4 py-1 rounded-lg mb-6 inline-flex items-center hover:bg-blue-400 focus:ring-4 focus:ring-blue-300 transition duration-300"
              >
                <div className="flex flex-col justify-center items-center">
                  <FaArrowLeft className="w-5 h-5 mb-1" />
                  <span className="text-sm font-medium">Vendors</span>
                </div>
              </button>

              {/* Search bar for courses under the selected vendor */}
              <div className="mb-6 max-w-lg mx-auto relative">
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchCourse}
                  onChange={(e) => setSearchCourse(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              </div>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {selectedVendor} Courses
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {isLoading ? (
                  <div className="col-span-full text-center py-10">
                    <FaSpinner className="animate-spin w-6 h-6 mx-auto text-gray-500" />
                    <span className="text-lg font-semibold text-gray-500">Loading courses...</span>
                  </div>
                ) : filteredCourses.length === 0 ? (
                  <li className="text-center text-gray-500 col-span-full text-xl font-medium">
                    No courses found for this vendor.
                  </li>
                ) : (
                  filteredCourses.map((course) => (
                    <li
                      key={course.originalName}
                      className="bg-white shadow-md rounded-lg overflow-hidden transform hover:scale-105 hover:shadow-2xl transition-all duration-300 ease-in-out"
                      itemScope
                      itemType="https://schema.org/Course"
                    >
                      <a
                        href={`/course/${course.originalName.replace('.html', '')}`}
                        className="block p-6"
                        itemProp="url"
                      >
                        <div className="flex items-center space-x-4">
                          <img
                            src={course.imageUrl}
                            alt={course.displayName}
                            className="w-10 h-10 rounded object-cover"
                          />
                          <h3 
                            className="text-sm font-semibold text-indigo-600 mb-2 hover:text-indigo-800 transition-colors duration-200"
                            itemProp="name"
                          >
                            {course.displayName}
                          </h3>
                          <meta itemProp="provider" content="Ikusasa Technology Solutions" />
                        </div>
                      </a>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Courses;
